import get from 'lodash/get';

import type { VendorDeviceSelection } from '../../components/vendorAndDeviceRadioList';
import { hasVendorDeviceSelection } from '../../components/vendorAndDeviceRadioList';
import { buildVendorDeviceMetaFormPatch, readVendorDeviceMeta, vendorDeviceMetaFromSelection } from '../../components/vendorDevice';
import type { ModelSubscriptionProviderPaths } from './types';

/** 从表单值恢复计价厂商设备选中项 */
export function readMsProviderVendorDevice(
  values: Record<string, unknown>,
  paths: Pick<ModelSubscriptionProviderPaths, 'pricing' | 'strategyName' | 'strategyDisplayName' | 'useSubResourcePricing'>,
): VendorDeviceSelection | null {
  const rawUseSub = get(values, paths.useSubResourcePricing);
  const useSubResourcePricing = typeof rawUseSub === 'boolean' ? rawUseSub : undefined;

  const meta = readVendorDeviceMeta(values, paths.pricing);
  if (hasVendorDeviceSelection(meta)) {
    return {
      vendor: meta?.vendor,
      device: meta?.device,
      vendorID: meta?.vendorID,
      deviceID: meta?.deviceID,
      deviceType: meta?.deviceType,
      billItemId: meta?.billItemId,
      quantityUnit: meta?.quantityUnit,
      ...(useSubResourcePricing !== undefined ? { useSubResourcePricing } : {}),
    };
  }

  const strategyName = String(get(values, paths.strategyName) ?? '').trim();
  const strategyDisplayName = String(get(values, paths.strategyDisplayName) ?? '').trim();
  if (!strategyName && !strategyDisplayName) return null;

  return {
    vendor: strategyDisplayName || undefined,
    // strategyName 提交语义为 deviceID，不能当作模型展示名
    deviceID: strategyName || undefined,
    ...(useSubResourcePricing !== undefined ? { useSubResourcePricing } : {}),
  };
}

/**
 * 计价厂商设备选择 → 策略字段 + providerPricing
 * - strategyName：仅 deviceID（禁止回退展示名）
 * - strategyDisplayName：vendor 展示名
 * - providerPricing：vendor/device 与 vendorID/deviceID 均写入（名称需额外提交时保留）
 */
export function buildMsProviderVendorDeviceFormPatch(
  selection: VendorDeviceSelection,
  paths: Pick<ModelSubscriptionProviderPaths, 'pricing' | 'strategyName' | 'strategyDisplayName'>,
): Record<string, unknown> {
  const deviceId = String(selection.deviceID ?? '').trim();
  const vendorName = String(selection.vendor ?? '').trim();

  return {
    [paths.strategyName]: deviceId || undefined,
    [paths.strategyDisplayName]: vendorName,
    ...buildVendorDeviceMetaFormPatch(paths.pricing, vendorDeviceMetaFromSelection(selection)),
  };
}
